import { atom } from 'jotai';

import { DisplayTemplate } from '@crossnokaye/typescript-sdk/models/components';

import CurrentFacilityAtoms from '@/state/current-facility.state';

/**
 * Session-only display template preview payload.
 * Survives in-app navigation; cleared on reload and facility change.
 */
export type DisplayTemplatePreviewSource = {
  drafts: Record<string, string>;
  baselines: Record<string, string>;
  keys: string[];
  /** `useListPrototypes().data` at last successful push (identity compare for stale). */
  prototypes: unknown;
};

export type DisplayTemplatePreviewStorage = {
  facilityShortname: string;
  jsonContent: string;
  /** Captured at last successful ux-config push; omitted by legacy import/download. */
  source?: DisplayTemplatePreviewSource;
} | null;

const displayTemplatePreviewStorageBaseAtom = atom<DisplayTemplatePreviewStorage>(null);
displayTemplatePreviewStorageBaseAtom.debugLabel = 'displayTemplatePreviewStorageBaseAtom';

/**
 * Facility-scoped preview storage. Returns null when the stored facility no longer matches.
 */
export const displayTemplatePreviewStorageAtom = atom(
  (get) => {
    const storage = get(displayTemplatePreviewStorageBaseAtom);
    const currentFacility = get(CurrentFacilityAtoms.facility);

    if (
      storage &&
      currentFacility?.shortName &&
      storage.facilityShortname !== currentFacility.shortName
    ) {
      return null;
    }

    return storage;
  },
  (_, set, newValue: DisplayTemplatePreviewStorage) => {
    set(displayTemplatePreviewStorageBaseAtom, newValue);
  }
);
displayTemplatePreviewStorageAtom.debugLabel = 'displayTemplatePreviewStorageAtom';

/**
 * Derived atom that parses the JSON string into a DisplayTemplate object.
 * Returns undefined if the JSON is invalid or null.
 */
export const displayTemplatePreviewAtom = atom<DisplayTemplate | undefined>((get) => {
  const storage = get(displayTemplatePreviewStorageAtom);
  if (!storage?.jsonContent) return undefined;

  try {
    return JSON.parse(storage.jsonContent) as DisplayTemplate;
  } catch {
    return undefined;
  }
});
displayTemplatePreviewAtom.debugLabel = 'displayTemplatePreviewAtom';
